// UI state (architecture §21.5, WP0): theme, accent, nav sheet, toasts, composer drafts, open thread, update prompt, online.
import { create } from 'zustand';

export type ThemeSetting = 'auto' | 'hell' | 'dunkel';
export type AccentSetting = 'silber' | 'eis';
export type ResolvedTheme = 'light' | 'dark';

export interface ToastAction { label: string; run: () => void }
export interface Toast {
  id: string;
  text: string;
  tone: 'info' | 'ok' | 'urgent';
  action?: ToastAction;
  /** Auto-dismiss after this many ms (default 5000; 0 = stays until dismissed). */
  ttlMs?: number;
}

/** localStorage keys; public/theme.js reads the first two before the first paint. */
export const THEME_KEY = 'hq:theme';
export const ACCENT_KEY = 'hq:accent';
export const DRAFT_PREFIX = 'hq:draft:';
/** WP4's unsent chat messages (`hq:failed:<threadId>`, components/chat/failed.ts) leave the device with the drafts. */
export const FAILED_SEND_PREFIX = 'hq:failed:';

function readKey(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage blocked or full: theme and drafts are conveniences only */
  }
}

function readTheme(): ThemeSetting {
  const v = readKey(THEME_KEY);
  return v === 'hell' || v === 'dunkel' ? v : 'auto';
}

function readAccent(): AccentSetting {
  return readKey(ACCENT_KEY) === 'eis' ? 'eis' : 'silber';
}

/** Drafts are one key per thread (`hq:draft:<threadId>`), so one thread never rewrites the others. */
function readDrafts(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) out[key.slice(DRAFT_PREFIX.length)] = localStorage.getItem(key) ?? '';
    }
  } catch {
    /* storage blocked */
  }
  return out;
}

/** Cleanups other modules register for logout (§8.4), such as WP4's unsent messages in memory. */
const logoutHooks = new Set<() => void>();

/** Runs `fn` on every logout (`clearAllDrafts`); returns the function that removes it. */
export function onLogout(fn: () => void): () => void {
  logoutHooks.add(fn);
  return () => {
    logoutHooks.delete(fn);
  };
}

/** Removes every composer draft and every unsent message from localStorage (logout, §8.4). */
export function removeStoredDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX) || key?.startsWith(FAILED_SEND_PREFIX)) keys.push(key);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* storage blocked */
  }
}

/** The theme the OS or the setting asks for (for code that must know, e.g. the orb). */
export function resolveTheme(t: ThemeSetting): ResolvedTheme {
  if (t === 'hell') return 'light';
  if (t === 'dunkel') return 'dark';
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * `hell` → data-theme="light", `dunkel` → data-theme="dark", `auto` → data-theme="auto" (§21.5, WP0 D2): design/final
 * renders Noir without an attribute and follows the OS only for data-theme="auto".
 */
export function themeAttribute(t: ThemeSetting): 'light' | 'dark' | 'auto' {
  return t === 'hell' ? 'light' : t === 'dunkel' ? 'dark' : 'auto';
}

/** §21.5: `eis` → data-accent="eis", `silber` → no attribute. design/final has no accent variant yet, so it has no visible effect. */
export function accentAttribute(a: AccentSetting): 'eis' | null {
  return a === 'eis' ? 'eis' : null;
}

function setRootAttribute(name: string, value: string | null): void {
  if (typeof document === 'undefined') return;
  if (value === null) document.documentElement.removeAttribute(name);
  else document.documentElement.setAttribute(name, value);
}

export function applyTheme(t: ThemeSetting): void {
  setRootAttribute('data-theme', themeAttribute(t));
}

export function applyAccent(a: AccentSetting): void {
  setRootAttribute('data-accent', accentAttribute(a));
}

export interface UiState {
  theme: ThemeSetting;
  setTheme(t: ThemeSetting): void;
  accent: AccentSetting;
  setAccent(a: AccentSetting): void;
  navSheetOpen: boolean;
  setNavSheetOpen(open: boolean): void;
  toasts: Toast[];
  toast(t: Omit<Toast, 'id'>): string;
  dismissToast(id: string): void;
  /** Composer drafts per thread, text only, persisted in localStorage under `hq:draft:<threadId>`. */
  drafts: Record<string, string>;
  setDraft(threadId: string, text: string): void;
  clearDraft(threadId: string): void;
  /** Logout: forgets every draft (state and storage). */
  clearAllDrafts(): void;
  /** The thread screen sets this so SSE reconnects refetch it (§21.4). */
  openThreadId: string | null;
  setOpenThread(id: string | null): void;
  updateAvailable: boolean;
  setUpdateAvailable(v: boolean): void;
  online: boolean;
  setOnline(v: boolean): void;
}

let toastSeq = 0;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

export const useUiStore = create<UiState>((set, get) => ({
  theme: readTheme(),
  setTheme(t) {
    writeKey(THEME_KEY, t);
    applyTheme(t);
    set({ theme: t });
  },
  accent: readAccent(),
  setAccent(a) {
    writeKey(ACCENT_KEY, a);
    applyAccent(a);
    set({ accent: a });
  },
  navSheetOpen: false,
  setNavSheetOpen: (open) => set({ navSheetOpen: open }),
  toasts: [],
  toast(t) {
    const id = `t${++toastSeq}`;
    set({ toasts: [...get().toasts.slice(-3), { ...t, id }] });
    const ttl = t.ttlMs ?? (t.action ? 10_000 : 5000);
    if (ttl > 0) timers.set(id, setTimeout(() => get().dismissToast(id), ttl));
    return id;
  },
  dismissToast(id) {
    const timer = timers.get(id);
    if (timer) clearTimeout(timer);
    timers.delete(id);
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
  drafts: readDrafts(),
  setDraft(threadId, text) {
    const drafts = { ...get().drafts };
    const value = text ? text.slice(0, 8000) : null;
    if (value) drafts[threadId] = value;
    else delete drafts[threadId];
    writeKey(DRAFT_PREFIX + threadId, value);
    set({ drafts });
  },
  clearDraft(threadId) {
    get().setDraft(threadId, '');
  },
  clearAllDrafts() {
    removeStoredDrafts();
    for (const fn of logoutHooks) {
      try {
        fn();
      } catch {
        /* a cleanup must not stop the logout */
      }
    }
    set({ drafts: {} });
  },
  openThreadId: null,
  setOpenThread: (id) => set({ openThreadId: id }),
  updateAvailable: false,
  setUpdateAvailable: (v) => set({ updateAvailable: v }),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  setOnline: (v) => set({ online: v }),
}));

/** Shortcut for non-React code (api.ts, sse.ts). */
export const toast = (t: Omit<Toast, 'id'>) => useUiStore.getState().toast(t);
